import React, { useMemo, useCallback, useContext } from 'react';
import { Input, Space } from 'antd';
import type { SearchProps as AntSearchProps } from 'antd/es/input';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { mergeStoreProps } from '../../base/storePropsUtils';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';
import { DataSetInheritContext } from '../../base/DataSetInheritContext';
import { resolveAffixRenderConfig, SPACE_ADDON_NOWRAP_STYLE } from '../../utils/inputAffixUtils';

const { Search } = Input;

/**
 * AndInputSearch Props 接口
 */
export interface AndInputSearchProps
  extends Omit<
      AntSearchProps,
      'onChange' | 'onSearch' | 'prefix' | 'suffix' | 'addonBefore' | 'addonAfter'
    >,
    BaseComponentProps,
    DataSetConfig {
  prefix?: string;
  suffix?: string;
  addonBefore?: string;
  addonAfter?: string;
  changeAction?: string;
  searchAction?: string;
  onChange?: (value: string) => void;
  onSearch?: (value: string) => void;
}

export const AndInputSearch: React.FC<AndInputSearchProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  changeAction,
  searchAction,
  onChange,
  onSearch,
  value: propValue,
  defaultValue,
  ...restProps
}) => {
  const ctx = useContext(DataSetInheritContext);
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const storeProps = useAndStore((state) =>
    sessionId && id ? state.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const storeValue = useMemo(() => {
    if (dataSetKey && dataSetField) return getDataSet(dataSetKey, dataSetField);
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  const displayValue = useMemo(() => storeValue ?? propValue ?? defaultValue, [storeValue, propValue, defaultValue]);

  const mergedProps = useMemo(
    () => mergeStoreProps(restProps as Record<string, unknown>, storeProps ?? undefined),
    [restProps, storeProps]
  );

  const { prefixIconNode, suffixIconNode, prefixAddonText, suffixAddonText, inputProps } = useMemo(() => {
    const m = mergedProps as Record<string, unknown>;
    const {
      prefix: p,
      suffix: s,
      addonBefore: ab,
      addonAfter: aa,
      ...rest
    } = m;
    const prefixConfig = resolveAffixRenderConfig({
      iconName: p === undefined || p === null ? undefined : String(p),
      label: ab === undefined || ab === null ? undefined : String(ab),
    });
    const suffixConfig = resolveAffixRenderConfig({
      iconName: s === undefined || s === null ? undefined : String(s),
      label: aa === undefined || aa === null ? undefined : String(aa),
    });
    return {
      prefixIconNode: prefixConfig.iconNode,
      suffixIconNode: suffixConfig.iconNode,
      prefixAddonText: prefixConfig.iconConfigured ? undefined : prefixConfig.labelText,
      suffixAddonText: suffixConfig.iconConfigured ? undefined : suffixConfig.labelText,
      inputProps: rest,
    };
  }, [mergedProps]);

  const inputDomProps = useMemo(
    () => prepareAntdDomProps(inputProps as Record<string, unknown>),
    [inputProps],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (onChange) {
        onChange(newValue);
        return;
      }
      if (dataSetKey && dataSetField) setDataSet(dataSetKey, dataSetField, newValue);
      if (changeAction && sessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, newValue, {
          id,
          dataSet,
          dataSetField,
        }, ctx);
        addCommand(sessionId, createCommand(actionName, arg));
      }
    },
    [onChange, dataSetKey, dataSetField, setDataSet, changeAction, sessionId, id, addCommand, ctx]
  );

  const handleSearch = useCallback(
    (value: string) => {
      if (onSearch) {
        onSearch(value);
        return;
      }
      if (searchAction && sessionId) {
        addCommand(sessionId, createCommand(searchAction, value));
      }
    },
    [onSearch, searchAction, sessionId, addCommand]
  );

  return (
    <Space.Compact style={{ width: '100%' }}>
      {prefixAddonText ? <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{prefixAddonText}</Space.Addon> : null}
      <Search
        {...(inputDomProps as object)}
        prefix={prefixIconNode}
        suffix={suffixIconNode}
        disabled={develop ? false : (inputDomProps as { disabled?: boolean }).disabled}
        value={displayValue}
        onChange={handleChange}
        onSearch={handleSearch}
      />
      {suffixAddonText ? <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{suffixAddonText}</Space.Addon> : null}
    </Space.Compact>
  );
};

export default AndInputSearch;
